import _ from 'lodash';
import { GraphQL, LONG_AGO, VantageNames } from '@/vantage/constants';
import { sqGraphQLApi } from '@/sdk';
import { AnyProperty } from '@/utilities.types';
import { Moment } from 'moment';
import { NUMBER_CONVERSIONS } from '@/main/app.constants';
import { SeeqAxiosRequestConfig } from '@/requests/axios.utilities';

const getAllContext = `
query Context($ids: [ContextIdInput!]!) {
  context(ids: $ids) {
    opaque {
      itemId
      datumId
      key
      context
      contextId
      createdAt
      creatorId
      updatedAt
      archived
      startTime
      endTime
      item{
        type
      }
      createdBy {
        guid
        fullName
      }
    }
    comments {
      itemId
      datumId
      context
      contextId
      createdAt
      updatedAt
      archived
      startTime
      endTime
      item{
        type
      }
      createdBy {
        guid
        fullName
      }
    }
    labels {
      itemId
      datumId
      context
      contextId
      createdAt
      creatorId
      updatedAt
      archived
      label
      categoryName
      startTime
      endTime
      item{
       type
      }
      createdBy {
        guid
        fullName
      }
    }
  }
}
`;

const getLabelContext = `
query Context($ids: [ContextIdInput!]!, $labelFilter: FilterInput) {
  context(ids: $ids, labelFilter: $labelFilter) {
    labels {
      itemId
      datumId
      context
      contextId
      createdAt
      creatorId
      updatedAt
      archived
      label
      categoryName
      startTime
      endTime
      item{
       type
      }
      createdBy {
        guid
        fullName
      }
    }
  }
}
`;

const getOpaqueContext = `
query Context($ids: [ContextIdInput!]!) {
  context(ids: $ids) {
    opaque {
      itemId
      datumId
      key
      context
      contextId
      archived
      startTime
      endTime
    }
  }
}
`;

const getTableQuery = `
query GetTable($id: String!, $filter: FilterInput, $limit: Int!, $columnsToInclude: [String!], $propertiesToInclude: [PropertiesForItemUUIDColumnInput!], $sorts: [SortInput!], $cursor: CursorInput) {
  table(id: $id, filter: $filter, limit: $limit, columnsToInclude: $columnsToInclude, propertiesToInclude: $propertiesToInclude, sorts: $sorts, cursor: $cursor) {
    rows
    headers {
      name
      type
    }
    hasMore,
    cursorKey
  }
}
`;

export { getTableQuery };

interface TableData {
  headers: { name: string; type: string }[];
  rows: any[][];
  cursorKey?: string;
}

interface ContextData {
  opaque: AnyProperty[];
  comments: AnyProperty[];
  labels: AnyProperty[];
}

interface LabelsData {
  labels: AnyProperty[];
}

export function tabulate({ rows, headers }: TableData): AnyProperty[] {
  const headerNames = headers.map((h) => h.name);
  return rows.map((row) => _.zipObject(headerNames, row));
}

async function graphql<T>(query: string, variables: AnyProperty, cancellationGroup?: string): Promise<T> {
  const config: SeeqAxiosRequestConfig = {};
  if (cancellationGroup) {
    config.cancellationGroup = cancellationGroup;
    config.cancelOnServer = true;
  }
  const {
    data: { errors, data },
  } = await sqGraphQLApi.graphql({ query, variables }, config);
  if (errors) {
    throw Error(JSON.stringify(errors));
  } else {
    return data;
  }
}

export async function getTable(
  tableId: string,
  {
    limit,
    columns,
    properties,
    filter,
    sorts,
    cursor,
  }: {
    limit?: number;
    columns: string[];
    properties?: AnyProperty[];
    filter?: any;
    sorts?: { columnName: string; ascending: boolean }[];
    cursor?: { cursorKey: string };
  },
  cancellationGroup?: string,
) {
  const data = await graphql<{ table: TableData }>(
    getTableQuery,
    { id: tableId, limit, columnsToInclude: columns, propertiesToInclude: properties, filter, sorts, cursor },
    cancellationGroup,
  );

  return { data: tabulate(data.table), cursorKey: data.table.cursorKey };
}

interface Event {
  properties: { name: string; value: any; unitOfMeasure?: string }[];
}

export function publish(tableId: string, events: Event[]) {
  return graphql('mutation PublishEvents($rowEvents: RowEventsInput!) { publishEvent(rowEvents: $rowEvents) }', {
    rowEvents: { tableDefinitionId: tableId, events: events.map((event) => ({ genericEvent: event })) },
  });
}

export async function context(itemId: string, datumId: string | undefined) {
  const data = await graphql<{ context: ContextData }>(getOpaqueContext, { ids: [{ itemId, datumIds: [datumId] }] });

  return data.context.opaque;
}

async function fetchContextForIdPairs<T>(
  idPairs: ([string, string] | [string])[],
  query: string,
  emptyResult: T,
  filters?: { labelFilter?: Filter; commentFilter?: Filter; opaqueFilter?: Filter },
  cancellationGroup?: string,
): Promise<T> {
  if (idPairs.length === 0) {
    return emptyResult;
  }
  const ids = _.chain(idPairs)
    .groupBy((idPair) => idPair[0])
    .map((idPairs, itemId) => {
      const datumIds = idPairs.flatMap((idPair) => idPair[1]).filter((datumId) => datumId);
      return {
        itemId,
        datumIds: datumIds.length > 0 ? datumIds : undefined,
      };
    })
    .value();

  const { labelFilter, commentFilter, opaqueFilter } = filters ?? {};
  const data = await graphql(query, { ids, labelFilter, commentFilter, opaqueFilter }, cancellationGroup);
  return (data as { context: T }).context;
}

export async function allContextForIdPairs(
  idPairs: ([string, string] | [string])[],
  cancellationGroup?: string,
): Promise<ContextData> {
  return fetchContextForIdPairs<ContextData>(
    idPairs,
    getAllContext,
    { opaque: [], comments: [], labels: [] },
    {},
    cancellationGroup,
  );
}

export async function labelContextForIdPairs(
  idPairs: ([string, string] | [string])[],
  filters?: { labelFilter?: Filter; commentFilter?: Filter; opaqueFilter?: Filter },
  cancellationGroup?: string,
): Promise<LabelsData> {
  return fetchContextForIdPairs<LabelsData>(idPairs, getLabelContext, { labels: [] }, filters, cancellationGroup);
}

interface CompositeFilter {
  compositeFilter: { filter1: Filter; filter2: Filter; operation: string };
}

type FilterValue = boolean | number | string;

export interface ValueFilter {
  valueFilter: { columnName: string; filterType: string; value: FilterValue | FilterValue[] };
}

export interface SimpleFilter {
  simpleFilter: { columnName: string; filterType: string };
}

export type Filter = CompositeFilter | ValueFilter | SimpleFilter;

function composeFilters(operation: string, filter: Filter, ...filters: (Filter | null)[]): Filter | CompositeFilter {
  const validFilters = filters.filter((filter) => filter) as Filter[];
  if (validFilters.length === 0) {
    return filter;
  } else if (validFilters.length === 1) {
    return { compositeFilter: { operation, filter1: filter, filter2: validFilters[0] } };
  } else {
    return validFilters.reduce(
      (compositeFilter, newFilter) => composeFilters(operation, compositeFilter, newFilter),
      filter,
    );
  }
}

export function andFilters(filter: Filter, ...filters: (Filter | null)[]): Filter | CompositeFilter {
  return composeFilters(GraphQL.Operations.And, filter, ...filters);
}

export function orFilters(filter: Filter, ...filters: (Filter | null)[]): Filter | CompositeFilter {
  return composeFilters(GraphQL.Operations.Or, filter, ...filters);
}

export function valueFilter(columnName: string, filterType: string, value: FilterValue | FilterValue[]): ValueFilter {
  return { valueFilter: { columnName, filterType, value } };
}

export function simpleFilter(columnName: string, filterType: string): SimpleFilter {
  return { simpleFilter: { columnName, filterType } };
}

function isNull(columnName: string): SimpleFilter {
  return simpleFilter(columnName, GraphQL.FilterTypes.IsNull);
}

export function isNotNull(columnName: string): SimpleFilter {
  return simpleFilter(columnName, GraphQL.FilterTypes.IsNotNull);
}

function notEqual(columnName: string, value: FilterValue): ValueFilter {
  return valueFilter(columnName, GraphQL.FilterTypes.NotIn, [value]);
}

export function equal(columnName: string, value: FilterValue): ValueFilter {
  return valueFilter(columnName, GraphQL.FilterTypes.In, [value]);
}

export function evidenceFilter(
  start: Moment,
  end: Moment,
  includeExtinct: boolean,
  includeArchived: boolean,
  lastFetched: Date = LONG_AGO,
) {
  return andFilters(
    valueFilter(
      VantageNames.Columns.Start,
      GraphQL.FilterTypes.LessThan,
      end.valueOf() * NUMBER_CONVERSIONS.NANOSECONDS_PER_MILLISECOND,
    ),
    orFilters(
      isNull(VantageNames.Columns.End),
      valueFilter(
        VantageNames.Columns.End,
        GraphQL.FilterTypes.GreaterThan,
        start.valueOf() * NUMBER_CONVERSIONS.NANOSECONDS_PER_MILLISECOND,
      ),
    ),
    valueFilter(VantageNames.Columns.UpdatedAt, GraphQL.FilterTypes.GreaterThanOrEqual, lastFetched.toISOString()),
    includeArchived ? null : isNull(VantageNames.Columns.DeletedAt),
    includeExtinct ? null : notEqual(VantageNames.Columns.LastCapsuleState, VantageNames.ColumnValues.Extinct),
  );
}

function evidenceContextFilter(
  start: Moment,
  end: Moment,
  lastFetched: Date,
  includeExtinct: boolean,
  updatedAtColumn: string,
  archivedColumn: string,
  includeArchived = false,
) {
  return andFilters(
    evidenceFilter(start, end, includeExtinct, includeArchived),
    valueFilter(updatedAtColumn, GraphQL.FilterTypes.GreaterThanOrEqual, lastFetched.toISOString()),
    includeArchived ? null : equal(archivedColumn, false),
  );
}

export function evidenceLabelsFilter(
  start: Moment,
  end: Moment,
  lastFetched: Date,
  includeExtinct: boolean,
  includeArchived = false,
) {
  return evidenceContextFilter(
    start,
    end,
    lastFetched,
    includeExtinct,
    VantageNames.TableQueryContextColumns.ContextLabelsUpdatedAt,
    VantageNames.TableQueryContextColumns.ContextLabelsArchived,
    includeArchived,
  );
}

export function evidenceOpaqueFilter(
  start: Moment,
  end: Moment,
  lastFetched: Date,
  includeExtinct: boolean,
  includeArchived = false,
) {
  return evidenceContextFilter(
    start,
    end,
    lastFetched,
    includeExtinct,
    VantageNames.TableQueryContextColumns.ContextOpaqueUpdatedAt,
    VantageNames.TableQueryContextColumns.ContextOpaqueArchived,
    includeArchived,
  );
}
